import { Layout } from '../components/layout/Layout'
import { GalleryGrid } from '../components/gallery/GalleryGrid'
import { AlbumCard } from '../components/album/AlbumCard'
import { useAlbums } from '../hooks/useAlbums'
import { useState } from 'react'
import { Grid, LayoutGrid, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { ShimmerSkeleton } from '../components/common/ShimmerSkeleton'

export const Home = () => {
  const [view, setView] = useState<'albums' | 'photos'>('albums')
  const [sortBy, setSortBy] = useState<'name' | 'createdAt'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const { isAuthenticated } = useAuthStore()
  const { data: albums, isLoading: albumsLoading } = useAlbums(undefined, { sortBy, sortOrder })

  const hasAlbums = albums && albums.length > 0

  return (
    <Layout>
      {/* View Toggle & Sort */}
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
            <button
              onClick={() => setView('albums')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                view === 'albums'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Albums</span>
            </button>
            <button
              onClick={() => setView('photos')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                view === 'photos'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Grid className="w-4 h-4" />
              <span className="hidden sm:inline">All Photos</span>
            </button>
          </div>
          
          {view === 'albums' && (
            <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'createdAt')}
                className="px-3 py-2 text-sm border-none bg-transparent focus:ring-0 min-w-[80px]"
              >
                <option value="name">Name</option>
                <option value="createdAt">Date</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-2 hover:bg-gray-100 rounded"
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          )}
        </div>

        {isAuthenticated && (
          <div className="flex items-center gap-2">
            <Link
              to="/admin/upload"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Upload Photos
            </Link>
          </div>
        )}
      </div>

      {/* Albums View */}
      {view === 'albums' && (
        <div>
          {albumsLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {[...Array(4)].map((_, i) => (
                <ShimmerSkeleton key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                  <div className="aspect-[4/3] bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-100 rounded w-1/2" />
                  </div>
                </ShimmerSkeleton>
              ))}
            </div>
          ) : hasAlbums ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {albums.map((album) => (
                <AlbumCard key={album.albumId} album={album} />
              ))}
              
              {/* Create New Album Card */}
              {isAuthenticated && (
                <Link
                  to="/admin/albums"
                  className="group flex flex-col items-center justify-center aspect-[4/3] bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                    <Plus className="w-8 h-8 text-gray-400 group-hover:text-blue-500" />
                  </div>
                  <span className="mt-4 text-sm font-medium text-gray-500 group-hover:text-blue-600">
                    Create New Album
                  </span>
                </Link>
              )}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LayoutGrid className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No albums yet</h3>
              <p className="text-gray-500 mb-6">Create your first album to organize your photos</p>
              {isAuthenticated && (
                <Link
                  to="/admin/albums"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <Plus className="w-5 h-5" />
                  Create First Album
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      {/* All Photos View */}
      {view === 'photos' && <GalleryGrid />}
    </Layout>
  )
}